import React, { useState, useEffect, useCallback } from "react";
import { useRedirectError } from "../utils/useRedirectError";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { buttonloginstyle, formatAuthError } from "../help/Utils";
import MARKETING_SITE_URL from "../API/MARKETING_SITE_URL";
import { GoogleSignInCustomButton } from "../components/GoogleSignInCustomButton";
import secure from "../assets/secure.png";
import { signup, googleSignupUrl, getPolicyVersions } from "../API/auth";
import { setSignedIn } from "../utils/authStorage";
import { setEmailVerificationState } from "../utils/emailVerificationStorage";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import ButtonLoadingLabel from "../components/ButtonLoadingLabel";

const NewSignUp = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    phoneNumber: "",
    acceptTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  // Task 164: the server refuses a signup that does not name the CURRENT
  // version of each policy, so these are read from the API rather than
  // hardcoded — a hardcoded copy would silently go stale the day the terms
  // are republished, and every signup would start failing.
  const [policyVersions, setPolicyVersions] = useState(null);
  const navigate = useNavigate();

  // A Google sign-up refused for missing agreement comes back here with the
  // reason in ?error=. Shown as sent — it is already written for the applicant.
  useRedirectError(useCallback((message) => setApiError(message), []));

  useEffect(() => {
    let cancelled = false;
    getPolicyVersions()
      .then((res) => {
        if (!cancelled) setPolicyVersions(res?.data?.currentVersions ?? null);
      })
      .catch(() => {
        if (!cancelled) setPolicyVersions(null);
      });
    return () => { cancelled = true; };
  }, []);

  const handleGoogleSignup = () => {
    if (!policyVersions) {
      setApiError("Still loading the Terms and Privacy Policy. Please try again in a moment.");
      return;
    }
    if (!formData.acceptTerms) {
      setErrors((prev) => ({ ...prev, acceptTerms: "You must accept the Terms." }));
      return;
    }
    // A new Google account is a signup, so acceptance travels with it. The
    // server signs these into the OAuth state and will not create an account
    // without them — it used to assume consent for Google signups.
    window.location.href = googleSignupUrl(policyVersions);
  };

  // Do NOT sanitize password
  const sanitizeAndFilter = (fieldName, value) => {
    if (fieldName === "password") return value;
    let newValue = value.replace(/<[^>]*>?/gm, "");
    switch (fieldName) {
      case "fullName":
        newValue = newValue.replace(/[^a-zA-Z ]/g, "");
        break;
      case "email":
        // newValue = newValue.replace(/[^a-zA-Z0-9@._-]/g, "");
        newValue = newValue.replace(/[^a-zA-Z0-9@._+-]/g, "");
        break;
      default:
        break;
    }
    return newValue;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const filteredValue =
      type === "checkbox" ? checked : sanitizeAndFilter(name, value);

    setFormData((prev) => ({
      ...prev,
      [name]: filteredValue,
    }));

    // Clear error when user corrects field
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // Phone number is managed by react-phone-number-input (E.164 value incl. country code)
  const handlePhoneChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      phoneNumber: value || "",
    }));
    setErrors((prev) => ({
      ...prev,
      phoneNumber: "",
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format.";
    }

    // if (!formData.password || formData.password.length < 8) {
    //   newErrors.password = "Password must be at least 8 characters.";
    // }

    if (!formData.password) {
      newErrors.password = "Password is required.";
    } else {
      const password = formData.password;
      if (password.length < 8) {
        newErrors.password = "Password must be at least 8 characters.";
      } else if (!/[A-Z]/.test(password)) {
        newErrors.password =
          "Password must contain at least one uppercase letter.";
      } else if (!/[a-z]/.test(password)) {
        newErrors.password =
          "Password must contain at least one lowercase letter.";
      } else if (!/[0-9]/.test(password)) {
        newErrors.password = "Password must contain at least one number.";
      } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        newErrors.password =
          "Password must contain at least one special character.";
      }
    }

    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      if (!isValidPhoneNumber(formData.phoneNumber)) {
        newErrors.phoneNumber =
          "Enter a valid phone number for the selected country.";
      }
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = "You must accept the Terms.";
    }

    // Without the current versions the server would refuse the signup anyway;
    // failing here says why, instead of surfacing a bare 400.
    if (!policyVersions) {
      newErrors.acceptTerms =
        "Could not load the current Terms and Privacy Policy. Please refresh and try again.";
    }

    return newErrors;
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setApiError("");
  //   const newErrors = validate();

  //   setErrors(newErrors);

  //   if (Object.keys(newErrors).length > 0) {
  //     console.warn("Validation errors:", newErrors);
  //     return;
  //   }

  //   setLoading(true);

  //   try {
  //     const payload = {
  //       fullName: formData.fullName,
  //       email: formData.email,
  //       password: formData.password,
  //       phoneNumber: formData.phoneNumber || "",
  //       acceptTerms: true,
  //     };

  //     console.log("🚀 Submitting signup payload:", payload);

  //     const response = await signup(payload);

  //     console.log("✅ Signup success:", response);

  //     alert("Account created successfully!");
  //     navigate("/answer-review");
  //   } catch (error) {
  //     console.error("❌ Signup failed:", error);
  //     const backendMessage =
  //       error.response?.data?.message ||
  //       error.response?.data?.error ||
  //       "Something went wrong. Please try again.";

  //     setApiError(backendMessage);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    const newErrors = validate();

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      console.warn("Validation errors:", newErrors);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        phoneNumber: formData.phoneNumber || "",
        acceptTerms: true,
        // Names the version accepted, so the record says WHICH text they
        // agreed to rather than only that they agreed to something.
        acceptedPolicies: policyVersions,
      };

      // The payload was logged here in full, password included, to anyone
      // with the browser console open.
      const response = await signup(payload);

      if (response?.data?.verificationRequired) {
        setEmailVerificationState({
          token: response.data.verificationToken,
          email: response.data.user.email,
          rememberMe: true,
          expiresAt: response.data.expiresAt,
          resendAvailableAt: response.data.resendAvailableAt,
          serverTime: response.data.serverTime,
          clientReceivedAt: Date.now(),
        });
        navigate("/verify-email");
        return;
      }

      // Save token (new signups get "keep me signed in" behavior by default)
      if (response?.data?.token) {
        // Task 149: session established as an HttpOnly cookie by this
        // response; only the non-credential marker is kept.
        setSignedIn();
        // localStorage.setItem("user", JSON.stringify(res.data.user));
      }

      // alert("Account created successfully!");

      // 👉 Check visa eligibility data in localStorage
      const eligibilityData = localStorage.getItem("visaEligibilityAnswers");
      const responses = localStorage.getItem("visaEligibilityResponses");

      if (eligibilityData || responses) {
        navigate("/answer-review");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      // Logging the error object printed axios's config.data with it, which
      // is the signup payload — password included.
      console.error("Signup failed:", error?.response?.status ?? error?.message);
      const verification = error.response?.data;
      if (verification?.verificationRequired && verification?.verificationToken) {
        setEmailVerificationState({
          token: verification.verificationToken,
          email: verification.user.email,
          rememberMe: true,
          expiresAt: verification.expiresAt,
          resendAvailableAt: verification.resendAvailableAt,
          serverTime: verification.serverTime,
          clientReceivedAt: Date.now(),
        });
        navigate("/verify-email", { state: { emailDeliveryError: true } });
        return;
      }
      // The policies were republished between loading this page and
      // submitting it, so what was accepted is no longer current. Re-read the
      // versions and say so, rather than showing a bare validation failure.
      if (error.response?.data?.code === "POLICY_ACCEPTANCE_REQUIRED") {
        const refreshed = error.response.data.currentVersions;
        if (refreshed) setPolicyVersions(refreshed);
        setApiError(
          "The Terms or Privacy Policy have been updated. Please review and accept them again.",
        );
        return;
      }

      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Something went wrong. Please try again.";
      setApiError(formatAuthError(backendMessage, "signup"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative login_main body_padding pb-[100px]">
      <h1 className="mb-1 text-center">Create your account</h1>
      <p className="text-center mb-8">
        Access your eligibility results and start your application
      </p>
      <form
        className="bg-white rounded-xl border border-gray-200 shadow-sm sm:p-[35px] p-5 rounded-2xl w-full max-w-[90%] sm:max-w-[500px] mx-auto login_form"
        onSubmit={handleSubmit}
      >
        {/* Full Name */}
        <div className="mb-5">
          <label className="mt-2 flex text-sm font-bold">Full Name</label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Enter your full name"
            className={`border rounded-lg w-full py-[14px] px-[16px] text-base ${
              errors.fullName ? "border-red-500" : "border-gray-200"
            }`}
          />
          {errors.fullName && (
            <span className="text-red-500 text-sm">{errors.fullName}</span>
          )}
        </div>

        {/* Email */}
        <div className="mb-5">
          <label className="mt-2 flex text-sm font-bold ">Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            className={`border rounded-lg w-full py-[14px] px-[16px] text-base ${
              errors.email ? "border-red-500" : "border-gray-200"
            }`}
          />
          {errors.email && (
            <span className="text-red-500 text-sm">{errors.email}</span>
          )}
        </div>

        {/* Password */}
        <div className="mb-5">
          <label className="mt-2 flex text-sm font-bold">Password</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Create password"
            className={`border rounded-lg w-full py-[14px] px-[16px] text-base ${
              errors.password ? "border-red-500" : "border-gray-200"
            }`}
          />
          {errors.password && (
            <span className="text-red-500 text-sm">{errors.password}</span>
          )}
          <p className="text-sm text-gray-500 mt-1">
            At least 8 characters, with one uppercase, one lowercase, one number, and one special character.
          </p>
        </div>

        {/* Phone Number */}
        <div className="mb-5">
          <label className="mt-2 flex text-sm font-bold">
            Phone Number (Optional)
          </label>
          <div
            className={`flex items-center border rounded-lg w-full py-[10px] px-[16px] text-base ${
              errors.phoneNumber ? "border-red-500" : "border-gray-200"
            }`}
          >
            <PhoneInput
              international
              defaultCountry="US"
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              placeholder="Enter phone number"
              className="lgv-phone-input w-full"
              numberInputProps={{
                className: "w-full border-0 outline-none focus:ring-0 text-base",
              }}
            />
          </div>
          {errors.phoneNumber && (
            <span className="text-red-500 text-sm">{errors.phoneNumber}</span>
          )}
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start gap-2 mt-5">
            <input
              type="checkbox"
              name="acceptTerms"
              checked={formData.acceptTerms}
              onChange={handleChange}
              className="mt-1"
            />
            <span className="leading-[22px] text-sm text-[#1B365D] ml-2">
              I agree to Let's Get Visa's{" "}
              <a
                target="_blanck"
                className="font-bold underline"
                href={`${MARKETING_SITE_URL}/privacy-policy/`}
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                target="_blanck"
                className="font-bold underline"
                href={`${MARKETING_SITE_URL}/terms-conditions/`}
              >
                Terms of Service
              </a>
            </span>
          </label>
          {errors.acceptTerms && (
            <span className="text-red-500 text-sm">{errors.acceptTerms}</span>
          )}
        </div>
        {apiError && (
          <div className="text-red-600 text-sm mb-4">{apiError}</div>
        )}

        {/* Submit */}
        <button
          type="submit"
          style={buttonloginstyle}
          disabled={loading}
          className={`w-full mt-7 ${loading ? "cursor-not-allowed" : ""}`}
        >
          {loading ? <ButtonLoadingLabel label="Submitting" /> : "Create Account"}
        </button>

        <div className="mt-6 w-full">
          <GoogleSignInCustomButton
            label="Continue with Google"
            onClick={handleGoogleSignup}
            disabled={loading}
          />
        </div>

        {/* Already have an account */}
        <div className="mt-6 pt-5 border-t border-gray-200">
          <p className="text-center font-semibold">
            Already have an account?{" "}
            <Link to="/" className="text-[#E76F51] font-bold">
              Login
            </Link>
          </p>
        </div>
      </form>

      <div className="mt-6 flex justify-center items-center">
        <img src={secure} alt="secure login" className="me-3" />
        Your information is encrypted and secure
      </div>
    </div>
  );
};

export default NewSignUp;
